import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ConfiguredDmvProviderResponse, DmvManualImportRequest, DmvManualImportResult, DmvProcessDmvRequestsResult, EnforcementService } from 'src/app/Services/enforcement.service';
import { ClassedViewColumn, Datalistviewcolumn } from 'src/app/shared/datalistview/columns/datalistviewcolumn';
import { DatalistviewComponent } from 'src/app/shared/datalistview/datalistview.component';
import { DmvStateFilterComponent } from 'src/app/shared/datalistview/filters/dmv-state-filter/dmv-state-filter.component';

@Component({
    selector: 'app-pending-dmv-records',
    templateUrl: './pending-dmv-records.component.html',
    styleUrls: ['./pending-dmv-records.component.scss'],
    standalone: false
})
export class PendingDmvRecordsComponent implements OnInit {
  @ViewChild(DatalistviewComponent) datalistview!: DatalistviewComponent;
  @ViewChild(DmvStateFilterComponent) dmvStateFilter?: DmvStateFilterComponent;
  @ViewChild('dmvUploadInput') dmvUploadInput?: ElementRef<HTMLInputElement>;

  public Provider: ConfiguredDmvProviderResponse | null = null;
  public ProcessButtonText: string = "Process";
  public Processing: boolean = false;
  public SelectedItems: any[] = [];
  public SelectedDmvState: string = "All";

  public ShowImportSummaryModal: boolean = false;
  public LastImportSummary: DmvManualImportResult | null = null;
  public LastImportErrorMessage: string | null = null;
  public LastImportErrorCode: string | null = null;

  public Columns: Array<Datalistviewcolumn> = [
    new Datalistviewcolumn("Citation Id", "Id"),
    new Datalistviewcolumn("Reference", "Reference"),
    new ClassedViewColumn("Citation Status", "CurrentStateKey"),
    new Datalistviewcolumn("Plate", "PlateNumber"),
    new Datalistviewcolumn("DMV", "DMVDescription"),
    new Datalistviewcolumn("Processed On", "DMVBeganProcessOnLocalFriendlyString"),
    new Datalistviewcolumn("Fetched On", "DMVFetchedOnLocalFriendlyString"),
    new DmvStateTagColumn("DMV State", "DMVState")
  ];

  constructor(private enforcementService: EnforcementService, private toastr: ToastrService) {
  }

  // Only Missing (0) items should be selectable; Processing/Fetched should not show a toggle.
  public CanSelectRow = (row: any): boolean => Number(row?.DMVState) === 0;

  public get SelectableRows(): any[] {
    const results: any[] = this.datalistview?.Data?.Results ?? [];
    return (results ?? []).filter(r => this.CanSelectRow(r));
  }

  public get HasSelectableRows(): boolean {
    return this.SelectableRows.length > 0;
  }

  async ngOnInit(): Promise<void> {
    try {
      this.Provider = await this.enforcementService.GetConfiguredDmvProvider();
      if (this.Provider?.IsConfigured && this.Provider.UIButtonText) {
        this.ProcessButtonText = this.Provider.UIButtonText;
      }
    } catch (e: any) {
      // If we can't resolve the provider, still allow the datalist to load.
      this.Provider = null;
    }

    // Default sort: newest first
    setTimeout(() => {
      if (this.datalistview && this.datalistview.Filter) {
        this.datalistview.Filter.OrderByProperty = "Id";
        this.datalistview.Filter.Ascending = false;
      }
    });
  }

  public SetDmvState(state: string): void {
    this.SelectedDmvState = state;
    this.SelectedItems = [];

    if (this.dmvStateFilter) {
      this.dmvStateFilter.StateChanged({ target: { value: state } });
    }

    // Trigger the datalist to re-run with the updated filters.
    this.datalistview?.FilterSet?.search();
  }

  public OnSelectBoxChanged(row: any): void {
    if (row?.Selected) {
      if (!this.SelectedItems.some(x => x.Id == row.Id)) {
        this.SelectedItems.push(row);
      }
    } else {
      this.SelectedItems = this.SelectedItems.filter(x => x.Id != row?.Id);
    }
  }

  public get AllItemsSelected(): boolean {
    const selectable = this.SelectableRows;
    if (!selectable || selectable.length === 0) {
      return false;
    }

    return selectable.every(r => this.SelectedItems?.some(s => s?.Id == r?.Id));
  }

  public ToggleSelectAll(): void {
    if (this.AllItemsSelected) {
      this.DeselectAll();
    } else {
      this.SelectAll();
    }
  }

  private SelectAll(): void {
    const selectable = this.SelectableRows;
    if (!selectable || selectable.length === 0) {
      this.SelectedItems = [];
      return;
    }

    // Keep row.Selected in sync so unchecking a row still triggers OnSelectBoxChanged correctly.
    selectable.forEach(r => r.Selected = true);
    this.SelectedItems = selectable.slice();
  }

  private DeselectAll(): void {
    const results: any[] = this.datalistview?.Data?.Results ?? [];
    results.forEach(r => r.Selected = false);
    this.SelectedItems = [];
  }

  public get ShowDmvActions(): boolean {
    // Only show selection toggles + action button if the provider fetch succeeded and the org is configured.
    return (this.Provider?.IsConfigured ?? false) === true;
  }

  public get ShowUpload(): boolean {
    return this.ShowDmvActions && (this.Provider?.AcceptsManualUploads ?? false) === true;
  }

  public get CanProcess(): boolean {
    return !this.Processing &&
      (this.Provider?.IsConfigured ?? false) &&
      this.SelectedItems.length > 0;
  }

  public async ProcessSelected(): Promise<void> {
    if (!this.Provider?.IsConfigured) {
      this.toastr.error("No DMV provider is configured for this organisation.");
      return;
    }
    if (this.SelectedItems.length == 0) {
      this.toastr.warning("Select one or more citations to process.");
      return;
    }

    this.Processing = true;
    try {
      const ids = this.SelectedItems.map(x => Number(x.Id)).filter(x => !isNaN(x));
      if(ids.length === 0) {
        this.toastr.warning("No valid citations were selected to process.");
        return;
      }
      const result: DmvProcessDmvRequestsResult = await this.enforcementService.ProcessPendingDmvRecords(ids);

      if (result?.ExportFile?.Bytes) {
        this.DownloadExport(result.ExportFile.Bytes, result.ExportFile.FileName, result.ExportFile.ContentType);
        this.toastr.success("Export generated.");
      } else {
        const count = result?.Results?.length ?? 0;
        this.toastr.success(`DMV processing completed (${count} results).`);
      }

      await this.ReloadList();
    } catch (e: any) {
      this.toastr.error("Failed to process DMV requests.");
      throw e;
    } finally {
      this.Processing = false;
    }
  }

  public OpenUpload(): void {
    // Reset so selecting the same file twice still fires change.
    if(this.dmvUploadInput?.nativeElement) {
      this.dmvUploadInput.nativeElement.value = '';
      this.dmvUploadInput.nativeElement.click();
    }
  }

  public async OnUploadSelected(evt: any): Promise<void> {
    const file: File | undefined = evt?.target?.files?.[0];
    if(!file) return;

    const lower = (file.name || '').toLowerCase();
    if(!lower.endsWith('.csv') && !lower.endsWith('.txt')) {
      this.toastr.error("Only .csv or .txt files are supported.");
      return;
    }

    this.Processing = true;
    try {
      const bytesBase64 = await this.ReadFileAsBase64(file);
      const req: DmvManualImportRequest = {
        FileName: file.name,
        ContentType: file.type || (lower.endsWith('.txt') ? 'text/plain' : 'text/csv'),
        Bytes: bytesBase64
      };

      const result: DmvManualImportResult = await this.enforcementService.ImportDmvResults(req);
      await this.ReloadList();

      this.LastImportSummary = result;
      this.LastImportErrorMessage = null;
      this.LastImportErrorCode = null;
      this.ShowImportSummaryModal = true;
    } catch(e: any) {
      // ApiService already shows a toast for 400s; we show details in the summary modal as well.
      const code = e?.error?.Error?.Code ?? e?.error?.error?.Error?.Code ?? null;
      const msg = e?.error?.Error?.Message ?? e?.error?.error?.Error?.Message ?? e?.message ?? "Failed to import DMV file.";

      this.LastImportSummary = null;
      this.LastImportErrorCode = code;
      this.LastImportErrorMessage = msg;
      this.ShowImportSummaryModal = true;
    } finally {
      this.Processing = false;
    }
  }

  public CloseImportSummaryModal(): void {
    this.ShowImportSummaryModal = false;
  }

  public get ImportRowsProcessed(): number {
    return this.LastImportSummary?.LinesProcessed ?? 0;
  }

  public get ImportRowsNotLinked(): number {
    return this.LastImportSummary?.UploadRowsNotLinkedToProcessingCitation ?? 0;
  }

  public get ImportRowsMatched(): number {
    // Import matches are by plate to citations currently in Processing/Pending.
    // (This is "rows matched", not necessarily unique plates, but it's what users care about for usefulness.)
    return Math.max(0, this.ImportRowsProcessed - this.ImportRowsNotLinked);
  }

  public get ImportCitationsUpdated(): number {
    return this.LastImportSummary?.CitationsUpdated ?? 0;
  }

  public get RemainingProcessingCitations(): number {
    // Citations still in "Processing" after this import (awaiting DMV info).
    return this.LastImportSummary?.ProcessingCitationsNotUpdated ?? 0;
  }

  private async ReloadList(): Promise<void> {
    // Clear selection and reload the current results after a server-side operation.
    this.SelectedItems = [];
    await this.datalistview?.Refresh();
  }

  private ReadFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        const buffer = reader.result as ArrayBuffer;
        resolve(this.ArrayBufferToBase64(buffer));
      };
      reader.readAsArrayBuffer(file);
    });
  }

  private ArrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;
    for(let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  }

  private DownloadExport(bytes: string | number[], fileName: string, contentType: string): void {
    let data: Uint8Array;

    if (Array.isArray(bytes)) {
      data = new Uint8Array(bytes);
    } else {
      // bytes is base64 when coming from ASP.NET Core JSON serialization of byte[]
      const binary = atob(bytes);
      data = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        data[i] = binary.charCodeAt(i);
      }
    }

    const blob = new Blob([data.buffer as ArrayBuffer], { type: contentType || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'dmv-export';
    a.click();
    window.URL.revokeObjectURL(url);
  }
}

class DmvStateTagColumn extends Datalistviewcolumn {
  constructor(header?: string, property?: string) {
    super(header, property);
    // Static tag markup only — no user data interpolated (FP-1259 rendering split).
    this.RendersHtml = true;
  }

  public override Render(item: any): string {
    const val = item?.[this.Property];
    switch (Number(val)) {
      case 0:
        return "<span class='tag is-warning'>Missing</span>";
      case 1:
        return "<span class='tag is-info'>Processing</span>";
      case 2:
        return "<span class='tag is-success'>Fetched</span>";
      default:
        return "<span class='tag'>-</span>";
    }
  }
}
